/**
 * CollectionGroupEditor - Create/Edit collection group
 * Elvis Aloha Collection Archive
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ChevronLeft, Save, Eye, Trash2, Plus, X, Image,
  Layers, BookOpen, Package, Archive, GripVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const fetchWithAuth = async (url, options = {}) => {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

const GROUP_TYPES = [
  { value: 'curated', label: 'Curated Collection', icon: Layers, description: 'Owner-curated thematic group' },
  { value: 'exhibition', label: 'Exhibition', icon: BookOpen, description: 'Historical exhibition grouping' },
  { value: 'category', label: 'Category', icon: Package, description: 'Category-based grouping' },
  { value: 'research', label: 'Research', icon: Archive, description: 'Research project grouping' },
  { value: 'acquisition', label: 'Acquisition', icon: Archive, description: 'Acquisition batch grouping' },
];

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public', description: 'Visible on public site' },
  { value: 'private', label: 'Private', description: 'Owner-only access' },
  { value: 'unlisted', label: 'Unlisted', description: 'Accessible via direct link' },
];

function ArtifactSelector({ groupId, currentArtifacts, onUpdate }) {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [availableArtifacts, setAvailableArtifacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  
  useEffect(() => {
    setArtifacts(currentArtifacts || []);
  }, [currentArtifacts]);
  
  const loadAvailableArtifacts = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth('/api/collection-items/manage');
      
      // Filter out already-added artifacts
      const currentIds = new Set(artifacts.map(a => a.id));
      const available = (data.artifacts || []).filter(a => !currentIds.has(a.id));
      setAvailableArtifacts(available);
      setShowPicker(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const addArtifacts = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      await fetchWithAuth(`/api/collection-groups/manage/${groupId}/artifacts`, {
        method: 'POST',
        body: JSON.stringify({ artifact_ids: selectedIds })
      });
      
      toast.success(`Added ${selectedIds.length} artifact(s)`);
      setShowPicker(false);
      setSelectedIds([]);
      onUpdate();
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const removeArtifact = async (artifactId) => {
    try {
      await fetchWithAuth(`/api/collection-groups/manage/${groupId}/artifacts/${artifactId}`, {
        method: 'DELETE'
      });
      
      setArtifacts(prev => prev.filter(a => a.id !== artifactId));
      toast.success('Artifact removed');
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-stone-900">Artifacts in Collection</h3>
        {groupId && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadAvailableArtifacts}
            disabled={loading}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Artifacts
          </Button>
        )}
      </div>
      
      {/* Current Artifacts */}
      {artifacts.length === 0 ? (
        <div className="text-center py-8 bg-stone-50 rounded-lg border border-dashed border-stone-300">
          <Package className="w-10 h-10 text-stone-400 mx-auto mb-2" />
          <p className="text-stone-500">No artifacts in this collection yet</p>
          {!groupId && (
            <p className="text-sm text-stone-400 mt-1">Save the collection first to add artifacts</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {artifacts.map((artifact, index) => (
            <div
              key={artifact.id}
              className="flex items-center gap-3 p-3 bg-white border border-stone-200 rounded-lg group"
            >
              <GripVertical className="w-4 h-4 text-stone-400 cursor-grab" />
              <span className="text-sm text-stone-500 w-6">{index + 1}</span>
              
              {artifact.primary_image?.thumbnail_url ? (
                <img
                  src={artifact.primary_image.thumbnail_url.startsWith('/') 
                    ? `${API_BASE}${artifact.primary_image.thumbnail_url}` 
                    : artifact.primary_image.thumbnail_url}
                  alt=""
                  className="w-12 h-12 rounded-lg object-cover border border-stone-200"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center">
                  <Image className="w-5 h-5 text-stone-400" />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <div className="font-medium text-stone-900 truncate">{artifact.title}</div>
                <div className="text-xs text-stone-500">{artifact.inventory_number}</div>
              </div>
              
              <button
                type="button"
                onClick={() => removeArtifact(artifact.id)}
                className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Artifact Picker Modal */}
      {showPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-stone-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-stone-900">Select Artifacts</h3>
              <button
                type="button"
                onClick={() => { setShowPicker(false); setSelectedIds([]); }}
                className="p-2 hover:bg-stone-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 max-h-96 overflow-y-auto">
              {availableArtifacts.length === 0 ? (
                <p className="text-center text-stone-500 py-8">No additional artifacts available</p>
              ) : (
                <div className="space-y-2">
                  {availableArtifacts.map(artifact => (
                    <label
                      key={artifact.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedIds.includes(artifact.id)
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-stone-200 hover:bg-stone-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(artifact.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(prev => [...prev, artifact.id]);
                          } else {
                            setSelectedIds(prev => prev.filter(id => id !== artifact.id));
                          }
                        }}
                        className="w-4 h-4 text-amber-600 rounded"
                      />
                      
                      {artifact.primary_image?.thumbnail_url ? (
                        <img
                          src={artifact.primary_image.thumbnail_url.startsWith('/')
                            ? `${API_BASE}${artifact.primary_image.thumbnail_url}`
                            : artifact.primary_image.thumbnail_url}
                          alt=""
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center">
                          <Image className="w-5 h-5 text-stone-400" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-stone-900 truncate">{artifact.title}</div>
                        <div className="text-xs text-stone-500">{artifact.inventory_number}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-stone-200 flex items-center justify-between bg-stone-50">
              <span className="text-sm text-stone-600">
                {selectedIds.length} selected
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowPicker(false); setSelectedIds([]); }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={addArtifacts}
                  disabled={selectedIds.length === 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Add Selected
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CollectionGroupEditor() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const isNew = !groupId || groupId === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    group_type: 'curated',
    short_description: '',
    description: '',
    historical_overview: '',
    hero_image_url: '',
    thumbnail_url: '',
    display_order: 0,
    is_featured: false,
    visibility: 'public',
    seo_title: '',
    seo_description: '',
  });
  
  useEffect(() => {
    if (!isNew) {
      fetchGroup();
    }
  }, [groupId, isNew]);
  
  const fetchGroup = async () => {
    try {
      const data = await fetchWithAuth(`/api/collection-groups/manage/${groupId}`);
      
      setFormData({
        name: data.name || '',
        slug: data.slug || '',
        group_type: data.group_type || 'curated',
        short_description: data.short_description || '',
        description: data.description || '',
        historical_overview: data.historical_overview || '',
        hero_image_url: data.hero_image_url || '',
        thumbnail_url: data.thumbnail_url || '',
        display_order: data.display_order || 0,
        is_featured: data.is_featured || false,
        visibility: data.visibility || 'public',
        seo_title: data.seo_title || '',
        seo_description: data.seo_description || '',
      });
      
      setArtifacts(data.artifacts_detail || []);
    } catch (err) {
      toast.error(err.message);
      navigate('/manage/collections');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Collection name is required');
      return;
    }
    
    setSaving(true);
    try {
      const url = isNew
        ? `/api/collection-groups/manage`
        : `/api/collection-groups/manage/${groupId}`;
      
      const result = await fetchWithAuth(url, {
        method: isNew ? 'POST' : 'PUT',
        body: JSON.stringify(formData)
      });
      
      toast.success(isNew ? 'Collection created!' : 'Collection updated!');
      
      if (isNew && result.group?.id) {
        navigate(`/manage/collections/${result.group.id}`);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700" />
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-4xl" data-testid="collection-group-editor">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/manage/collections"
          className="p-2 hover:bg-stone-100 rounded-lg"
        >
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-stone-900">
            {isNew ? 'New Collection' : 'Edit Collection'}
          </h1>
        </div>
        {!isNew && formData.slug && (
          <a
            href={`/collection/groups/${formData.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-stone-600 hover:text-amber-700"
          >
            <Eye className="w-4 h-4" />
            View Public Page
          </a>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info */}
        <section className="bg-white rounded-xl border border-stone-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-stone-900 border-b border-stone-100 pb-3">
            Basic Information
          </h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Collection Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., The Graceland Display Collection"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                URL Slug
              </label>
              <Input
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="Auto-generated if empty"
              />
              <p className="text-xs text-stone-500 mt-1">
                /collection/groups/{formData.slug || 'auto-generated'}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Display Order
              </label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) => handleChange('display_order', parseInt(e.target.value) || 0)}
                min="0"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Short Description
            </label>
            <Input
              value={formData.short_description}
              onChange={(e) => handleChange('short_description', e.target.value)}
              placeholder="Brief summary for cards (max 500 chars)"
              maxLength={500}
            />
          </div>
        </section>
        
        {/* Type & Visibility */}
        <section className="bg-white rounded-xl border border-stone-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-stone-900 border-b border-stone-100 pb-3">
            Type & Visibility
          </h2>
          
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Collection Type
              </label>
              <div className="space-y-2">
                {GROUP_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <label
                      key={type.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.group_type === type.value
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-stone-200 hover:bg-stone-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="group_type"
                        value={type.value}
                        checked={formData.group_type === type.value}
                        onChange={(e) => handleChange('group_type', e.target.value)}
                        className="sr-only"
                      />
                      <Icon className="w-5 h-5 text-stone-600" />
                      <div className="flex-1">
                        <div className="font-medium text-stone-900">{type.label}</div>
                        <div className="text-xs text-stone-500">{type.description}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Visibility
              </label>
              <div className="space-y-2">
                {VISIBILITY_OPTIONS.map(option => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.visibility === option.value
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-stone-200 hover:bg-stone-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="visibility"
                      value={option.value}
                      checked={formData.visibility === option.value}
                      onChange={(e) => handleChange('visibility', e.target.value)}
                      className="sr-only"
                    />
                    <div>
                      <div className="font-medium text-stone-900">{option.label}</div>
                      <div className="text-xs text-stone-500">{option.description}</div>
                    </div>
                  </label>
                ))}
              </div>
              
              <div className="mt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => handleChange('is_featured', e.target.checked)}
                    className="w-4 h-4 text-amber-600 rounded"
                  />
                  <span className="text-sm font-medium text-stone-700">Featured Collection</span>
                </label>
                <p className="text-xs text-stone-500 ml-6 mt-1">
                  Featured collections appear prominently on the homepage
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Detailed Content */}
        <section className="bg-white rounded-xl border border-stone-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-stone-900 border-b border-stone-100 pb-3">
            Detailed Content
          </h2>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Full Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Detailed description of the collection..."
              rows={4}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Historical Overview
            </label>
            <textarea
              value={formData.historical_overview}
              onChange={(e) => handleChange('historical_overview', e.target.value)}
              placeholder="Historical context and background..."
              rows={4}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
        </section>
        
        {/* Images */}
        <section className="bg-white rounded-xl border border-stone-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-stone-900 border-b border-stone-100 pb-3">
            Images
          </h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Hero Image URL
              </label>
              <Input
                value={formData.hero_image_url}
                onChange={(e) => handleChange('hero_image_url', e.target.value)}
                placeholder="https://..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Thumbnail URL
              </label>
              <Input
                value={formData.thumbnail_url}
                onChange={(e) => handleChange('thumbnail_url', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
        </section>
        
        {/* SEO */}
        <section className="bg-white rounded-xl border border-stone-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-stone-900 border-b border-stone-100 pb-3">
            SEO Settings
          </h2>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              SEO Title
            </label>
            <Input
              value={formData.seo_title}
              onChange={(e) => handleChange('seo_title', e.target.value)}
              placeholder="Custom title for search engines"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              SEO Description
            </label>
            <textarea
              value={formData.seo_description}
              onChange={(e) => handleChange('seo_description', e.target.value)}
              placeholder="Meta description for search engines (max 160 chars)"
              rows={2}
              maxLength={160}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
        </section>
        
        {/* Artifacts */}
        {!isNew && (
          <section className="bg-white rounded-xl border border-stone-200 p-6">
            <ArtifactSelector
              groupId={groupId}
              currentArtifacts={artifacts}
              onUpdate={fetchGroup}
            />
          </section>
        )}
        
        {/* Actions */}
        <div className="flex items-center justify-between pt-4">
          <Link
            to="/manage/collections"
            className="text-stone-600 hover:text-stone-900"
          >
            Cancel
          </Link>
          
          <Button
            type="submit"
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : (isNew ? 'Create Collection' : 'Save Changes')}
          </Button>
        </div>
      </form>
    </div>
  );
}
